import { handleCreateSO } from "../common/netsuite";
import { handleCreatePO } from "../../services/mulesoft"; // Import the PO creation handler
import Tooltip from "@mui/material/Tooltip";
import { CircularProgress, IconButton } from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import TaskIcon from "@mui/icons-material/Task";
import isCountryRestricted from "../../helpers/isCountryRestricted";
import { useState } from "react";
import isCountryWhitelisted from "../../helpers/isCountryWhitelisted";

const validateSONumber = (SONumber) => {
  if (SONumber && SONumber !== "") return true;
  else return false;
};

// validate po number:
const validatePONumber = (PONumber) => {
  if (PONumber && PONumber !== "") return true;
  else return false;
};

const ActionComponent = (props) => {
  const [loading, setLoading] = useState(false);
  const { actions, row, handleReload } = props;
  const actionData = ["create_so"];
  const tooltipTitle = loading ? "Creating ..." : getTooltipTitle(row);

  /**
   * Function to handle the creation of Sales Order
   * This function will be called when the user clicks on the create SO button
   * It will call the handleCreateSO function to create the SO
   * and then reload the existing orders to reflect the changes
   * @param {any} row - The row data containing the necessary information to create the SO
   * @returns {Promise<void>}
   * @throws {Error} If there is an error while creating the SO
   */
  const handleCreateSalesOrder = async (row) => {
    try {
      setLoading(true); // Set loading state to true
      await handleCreateSO(row);
      await handleReload(); // Reload existing orders after creating SO
    } catch (err) {
      console.error("Error while creating Sales Order", err);
    } finally {
      setLoading(false); // Ensure loading state is reset
    }
  };

  /**
   * Function to handle the creation of Purchase Order (PO)
   * This function is a placeholder for PO creation logic, similar to handleCreateSO.
   * It should call the PO creation handler and reload the orders to reflect changes.
   * @param {any} row - The row data containing the necessary information to create the PO
   * @returns {Promise<void>}
   * @throws {Error} If there is an error while creating the PO
   */
  const handleCreatePurchaseOrder = async (row) => {
    try {
      setLoading(true); // Set loading state to true
      await handleCreatePO(row); // Implement this function in netsuite.js similar to handleCreateSO
      await handleReload(); // Reload existing orders after creating PO
    } catch (err) {
      console.error("Error while creating Purchase Order", err);
    } finally {
      setLoading(false); // Ensure loading state is reset
    }
  };

  /**
  * Function to handle the creation of an order (Sales Order or Purchase Order) based on country restrictions and whitelist status.
  * Handles the creation of an order (Sales Order or Purchase Order) based on country restrictions and whitelist status.
  *
  * If the country is restricted and whitelisted, a Purchase Order (PO) is created.
  * Otherwise, a Sales Order (SO) is created.
  *
  * @async
  * @function
  * @param {Object} row - The row data containing order information, including country and order numbers.
  * @returns {Promise<void>} Resolves when the order creation process is complete.
  * @throws {Error} If there is an error during order creation.
   */
  const handleCreateOrder = async (row) => {
    try {
      // If restricted but whitelisted, create PO; else create SO
      if (isCountryRestricted(row?.country_1) && isCountryWhitelisted(row?.country_1)) {
        await handleCreatePurchaseOrder(row);
      } else {
        await handleCreateSalesOrder(row);
      }
    } catch (err) {
      console.error("Error while creating order", err);
    }
  };

  /**
   * Function to handle the button disabled state
   * This function will check if the SO number is valid and if the country is restricted
   * If either condition is true, the button will be disabled
   * @param {any} row - The row data containing the necessary information to check the button state
   * @returns {boolean} - Returns true if the button should be disabled, false otherwise
   */
  const handleBtnDisabled = (row) => {
    if (validateSONumber(row?.so_number)) return true;
    if (validatePONumber(row?.po_number)) return true;
    // If country is restricted, only allow if whitelisted
    if (isCountryRestricted(row?.country_1)) {
      if (isCountryWhitelisted(row?.country_1)) return false;
      return true;
    }
    // For unrestricted countries, allow
    return false;
  };

  return (
    // <div style={{ display: "flex" }}>
    <div>
      {actions &&
        actions.length &&
        actions.map((action, index) => {
          return (
            <div key={index}>
              {action === actionData[0] ? (
                <Tooltip // New implementation by <pratik.sonar@rahisystems.com>
                  disableFocusListener
                  disableTouchListener
                  title={tooltipTitle}
                  placement="top"
                  arrow
                >
                  {/* Added span to display tooltip for disable button */}
                  <span>
                    <IconButton
                      color="primary"
                      id="create_order_btn"
                      onClick={() => handleCreateOrder(row)}
                      disabled={handleBtnDisabled(row) || loading}
                    >
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        // Show TaskIcon if either SO or PO numbers exist, else show NoteAddOutlinedIcon
                        row?.so_number || row?.po_number ? (
                          <TaskIcon style={{ fontSize: "26px" }} />
                        ) : (
                          <NoteAddOutlinedIcon style={{ fontSize: "26px" }} />
                        )
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </div>
          );
        })}
    </div>
  );
};

export default ActionComponent;

// Update logic to if country is whitelisted or country is restricted then enable the button
const getTooltipTitle = (row) => {
  if (validateSONumber(row?.so_number)) return row?.so_number;
  if (validatePONumber(row?.po_number)) return row?.po_number;
  if (isCountryRestricted(row?.country_1) && !isCountryWhitelisted(row?.country_1)) return "Restricted by Policy";
  else return "Create Order";
};
